import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DocumentIcon, Drawer, EmptyState, ErrorState, LayersIcon, LoadingState, TrashIcon, useConfirm, useToast } from "@signet/ui";
import { useMe } from "@signet/web-kit";
import { amountText, createItem, deleteItem, exKeys, fetchItems, updateItem, type Item, type ItemPatch } from "./api.ts";
import { CategorySelect, CurrencySelect, DayPicker, UnitSelect } from "./controls.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   The line items, as a table edited where it stands (Jay, 2026-10-21): a
   name, then a category, an expiry rate, the next expiry and a price with
   its currency. Text saves when the box is left; menus save when picked.
   The notes button opens the item's notes in a sidebar.
   ────────────────────────────────────────────────────────────────────────── */

export function useSaveItem() {
  const client = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ItemPatch }) => updateItem(id, patch),
    onSuccess: () => client.invalidateQueries({ queryKey: exKeys.all }),
    onError: (error) => toast.error("Couldn't save that", (error as Error).message),
  });
}

const numberOrNull = (text: string) => {
  const n = Number(text.replace(/,/g, "").trim());
  return text.trim() === "" || !Number.isFinite(n) ? null : n;
};

function ItemRow({ item, onNotes }: { item: Item; onNotes: () => void }) {
  const { can } = useMe();
  const editable = can("expiries.item:edit");
  const save = useSaveItem();
  const client = useQueryClient();
  const toast = useToast();
  const { confirm, element } = useConfirm();
  const patch = (p: ItemPatch) => save.mutate({ id: item.id, patch: p });

  return (
    <div className="table-row ex-row">
      <input
        key={`n${item.name}`}
        className="ex-cell-input ex-name"
        defaultValue={item.name}
        aria-label="Name"
        disabled={!editable}
        onBlur={(e) => {
          const name = e.target.value.trim();
          if (!name) e.target.value = item.name;
          else if (name !== item.name) patch({ name });
        }}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      />
      <CategorySelect value={item.category?.id ?? null} onChange={(categoryId) => patch({ categoryId })} disabled={!editable} />
      <span className="ex-pair">
        <input
          key={`r${item.rateCount}`}
          className="ex-cell-input ex-num"
          inputMode="numeric"
          defaultValue={item.rateCount ?? ""}
          placeholder="30"
          aria-label="Expiry rate"
          disabled={!editable}
          onBlur={(e) => {
            const n = numberOrNull(e.target.value);
            const rateCount = n === null ? null : Math.max(1, Math.round(n));
            if (rateCount !== item.rateCount) patch({ rateCount, ...(rateCount && !item.rateUnit ? { rateUnit: "days" as const } : {}) });
            else e.target.value = String(item.rateCount ?? "");
          }}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        />
        <UnitSelect value={item.rateUnit ?? (item.rateCount ? "days" : null)} onChange={(rateUnit) => patch({ rateUnit })} disabled={!editable} />
      </span>
      <DayPicker label="Next expiry" value={item.nextExpiry} onChange={(nextExpiry) => patch({ nextExpiry })} disabled={!editable} />
      <span className="ex-pair">
        <input
          key={`p${item.price}`}
          className="ex-cell-input ex-num"
          inputMode="decimal"
          defaultValue={item.price === null ? "" : amountText(item.price)}
          placeholder="0.00"
          aria-label="Price"
          disabled={!editable}
          onBlur={(e) => {
            const n = numberOrNull(e.target.value);
            const price = n === null ? null : Math.max(0, Math.round(n * 100) / 100);
            if (price !== item.price) patch({ price });
            else e.target.value = item.price === null ? "" : amountText(item.price);
          }}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        />
        <CurrencySelect value={item.currency} onChange={(currency) => patch({ currency })} disabled={!editable} compact />
      </span>
      <span className="ex-tools">
        <button type="button" className={`icon-button ghost small${item.notes.trim() ? " on" : ""}`} aria-label={item.notes.trim() ? "Notes" : "Add notes"} title="Notes" onClick={onNotes}>
          <DocumentIcon size={13} />
        </button>
        {can("expiries.item:delete") ? (
          <button
            type="button"
            className="icon-button ghost small danger"
            aria-label={`Remove ${item.name}`}
            title="Remove"
            onClick={async () => {
              if (!(await confirm({ title: `Remove ${item.name}?`, body: "Its payments stay in the history.", confirmLabel: "Remove", danger: true }))) return;
              try {
                await deleteItem(item.id);
                await client.invalidateQueries({ queryKey: exKeys.all });
              } catch (error) {
                toast.error("Couldn't remove it", (error as Error).message);
              }
            }}
          >
            <TrashIcon size={13} />
          </button>
        ) : null}
      </span>
      {element}
    </div>
  );
}

function AddRow() {
  const [name, setName] = useState("");
  const client = useQueryClient();
  const toast = useToast();
  const add = useMutation({
    mutationFn: () => createItem({ name: name.trim() }),
    onSuccess: async () => {
      setName("");
      await client.invalidateQueries({ queryKey: exKeys.items });
    },
    onError: (error) => toast.error("Couldn't add it", (error as Error).message),
  });
  return (
    <form
      className="ex-add"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() && !add.isPending) add.mutate();
      }}
    >
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Add a line item, then press Enter" aria-label="New line item" />
    </form>
  );
}

function NotesDrawer({ item, onClose }: { item: Item | null; onClose: () => void }) {
  const { can } = useMe();
  const save = useSaveItem();
  return (
    <Drawer open={!!item} onClose={onClose} title={item?.name ?? ""} subtitle="Notes" narrow>
      {item ? (
        <div className="ex-notes-wrap">
        <textarea
          key={item.id}
          className="ex-notes"
          defaultValue={item.notes}
          placeholder="Logins, what it's for, who set it up…"
          aria-label="Notes"
          disabled={!can("expiries.item:edit")}
          autoFocus
          onBlur={(e) => e.target.value !== item.notes && save.mutate({ id: item.id, patch: { notes: e.target.value } })}
        />
        </div>
      ) : null}
    </Drawer>
  );
}

export function ItemsPanel() {
  const { can } = useMe();
  const list = useQuery({ queryKey: exKeys.items, queryFn: fetchItems, refetchInterval: 60_000 });
  const [notesFor, setNotesFor] = useState<string | null>(null);
  const items = list.data?.items ?? [];

  return (
    <section className="panel ex-items">
      <div className="panel-head">
        <h2>Line items</h2>
      </div>
      <div className="panel-body flush">
        {list.isLoading ? (
          <LoadingState />
        ) : list.isError ? (
          <ErrorState title="Couldn't load the line items" onRetry={() => list.refetch()} />
        ) : (
          <div className="table">
            <div className="table-row head ex-row">
              <span>Name</span>
              <span>Category</span>
              <span>Expiry rate</span>
              <span>Next expiry</span>
              <span>Price</span>
              <span />
            </div>
            {items.map((item) => <ItemRow key={item.id} item={item} onNotes={() => setNotesFor(item.id)} />)}
            {!items.length && !can("expiries.item:create") ? <EmptyState icon={<LayersIcon size={18} />} title="No line items yet" /> : null}
          </div>
        )}
        {can("expiries.item:create") && !list.isLoading ? <AddRow /> : null}
      </div>
      <NotesDrawer item={items.find((i) => i.id === notesFor) ?? null} onClose={() => setNotesFor(null)} />
    </section>
  );
}
